import styled, { css } from "styled-components";
import { getLocale } from "react-i18nify";

import device from "src/styles/device";
import font from "src/styles/font";

import {
  TextField,
  Grid,
  Box,
  Autocomplete,
  createFilterOptions,
  IconButton,
  FormControlLabel,
  InputAdornment,
  Checkbox,
} from "@mui/material";

import Button from "src/components/core/Button";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

import useModal from "src/hooks/useModal";
import useDeviceSize from "src/hooks/useDeviceSize";
import { convertPrice } from "src/common";

import ModalAddition from "src/modules/quotation-draft/CreateQuoDraft/AdditionModal";
import EditSheet from "src/modules/quotation-draft/CreateQuoDraft/ProductSheet/EditSheet";

const WrapperGrid = styled.div`
  width: 100%;
  /* margin-top: 24px; */
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  border-radius: 24px;
  background-color: var(--white-color);
  /* box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1); */

  ::-webkit-scrollbar {
    width: 5px;
    height: 7px;
    display: none;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: var(--grey-color);
    border-radius: 100px;
  }
  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: var(--grey-color);
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  @media ${device.tabletL} {
    padding-bottom: 0;
    margin-bottom: 24px;

    ${({ empty }) =>
      empty
        ? css`
            border-radius: 24px 24px 0 0;
            box-shadow: none;
          `
        : css`
            border-radius: 24px;
            box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.1);
          `}
  }
`;

const TextDetail = styled(Box)`
  width: 100%;
  color: ${({ color }) =>
    color === "grey" ? "var(--darkgrey-color)" : "var(--secondary-color)"};
  font-size: ${font.size.sm};
  font-weight: 400;
  margin-bottom: 8px;

  :last-child {
    margin-bottom: 0;
  }

  ${({ nowrap }) =>
    nowrap
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          white-space: pre-line;
          word-break: break-word;
        `}/* @media ${device.tabletL} {
    font-size: ${font.size.sm};
  } */
`;

const ServiceDataGrid = ({
  isDetail,
  formErrors,
  formData,
  onChange,
  onAppendElement, // add default product
  onRemoveElement,
  dataServiceType,
  initialConfirm,
  setConfirmInfo,
}) => {
  const [deviceWidth] = useDeviceSize();

  const { getModalState, open, close } = useModal();

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  return (
    <>
      <WrapperGrid empty={formData?.service_list[0]?.service_type === null}>
        <Grid
          container
          sx={{
            m: 0,
            width: "100%",
            minWidth: {
              xs: "670px",
              sm: "710px",
              md: "unset",
            },
          }}
        >
          {/* Header */}
          <Grid
            item
            xs={12}
            sx={{
              px: { lg: 5, xs: 3 },
              py: 2,
              backgroundColor: "var(--darkwhite-color)",
              borderRadius: "24px 24px 0 0",
              fontSize: font.size.sm,
            }}
          >
            <Grid
              container
              columnSpacing={{ xs: 2, lg: 3 }}
              sx={{
                textAlign: "center",
                alignItems: "center",
              }}
            >
              <Grid item xs={4} md={6} sx={{ textAlign: "left" }}>
                การบริการ
              </Grid>

              <Grid item xs={1.9} md={1.3}>
                จำนวน
              </Grid>

              <Grid item xs={1.9} md={1.4}>
                ราคาต่อหน่วย
              </Grid>

              <Grid item xs={1.9} md={1.3}>
                ส่วนลด
              </Grid>

              <Grid item xs={2.3} md={1.6}>
                ราคารวมทั้งหมด
              </Grid>
            </Grid>
          </Grid>

          {formData?.service_list?.length > 0 &&
            formData.service_list.map((product, index) => (
              <>
                {deviceWidth > 835 ? (
                  <>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        px: { lg: 5, xs: 3 },
                        py: 3,
                        fontSize: font.size.sm,
                        borderBottom: "1px solid var(--lightgrey-color)",
                      }}
                      key={`service-desktop-${index}`}
                    >
                      <Grid
                        container
                        columnSpacing={{ xs: 2, lg: 3 }}
                        sx={{
                          fontSize: font.size.md,
                          textAlign: "center",
                          ".MuiAutocomplete-root": {
                            margin: 0,
                          },
                          ".MuiTextField-root": {
                            borderRadius: "24px",
                          },
                          ".MuiOutlinedInput-root": {
                            borderRadius: "24px",
                          },
                          ".MuiOutlinedInput-input": {
                            py: "10px",
                            textAlign: "center",
                          },
                          ".MuiInputBase-inputMultiline": {
                            p: 0,
                            textAlign: "left",
                          },
                          ".MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input":
                            {
                              px: 0,
                            },
                        }}
                      >
                        <Grid
                          item
                          xs={6}
                          sx={{
                            ".MuiOutlinedInput-input": {
                              textAlign: "left",
                            },
                          }}
                        >
                          <Autocomplete
                            aria-required={true}
                            id="service-type-select"
                            isOptionEqualToValue={(option, value) => {
                              return option._id === value._id;
                            }}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                key={`service-quotation-${option._id}`}
                              >
                                {`${option?.service_code} (${
                                  option?.service_name
                                }/${
                                  getLocale() === "th"
                                    ? option?.service_unit?.unit_th
                                    : option?.service_unit?.unit_en
                                })`}
                              </li>
                            )}
                            options={dataServiceType}
                            value={product.service_type}
                            onChange={(event, newValue) => {
                              onChange(
                                null,
                                `service_list.${index}.service_type`,
                                newValue,
                              );
                            }}
                            getOptionLabel={(option) => {
                              return `${option?.service_code} (${
                                option?.service_name
                              }/${
                                getLocale() === "th"
                                  ? option?.service_unit?.unit_th
                                  : option?.service_unit?.unit_en
                              })`;
                            }}
                            filterOptions={filterOptions}
                            sx={{
                              my: 2,
                              width: "100%",
                              background: "#fff",
                              ".MuiOutlinedInput-root": {
                                paddingLeft: "16px",
                              },
                              ".MuiOutlinedInput-input": {
                                textAlign: "left",
                              },
                              // "label[data-shrink=false]": {
                              //   opacity: 0.65,
                              // },
                            }}
                            renderInput={(params) => (
                              <TextField
                                className="TextField-without-border-radius"
                                {...params}
                                placeholder="เพิ่มการบริการ"
                                error={
                                  !!formErrors[
                                    `service_list[${index}].service_type`
                                  ]
                                }
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={1.3}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            sx={{
                              pt: "9px",
                            }}
                            value={product?.volume}
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                onChange(e, `service_list.${index}.volume`);
                            }}
                            error={
                              !!formErrors[`service_list[${index}].volume`]
                            }
                          />
                        </Grid>

                        <Grid item xs={1.4}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            sx={{
                              pt: "9px",
                              ".MuiInputAdornment-root": {
                                position: "absolute",
                                left: "14px",
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ฿
                                </InputAdornment>
                              ),
                            }}
                            value={product?.price}
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                onChange(
                                  null,
                                  `service_list.${index}.price`,
                                  e.target.value,
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                onChange(
                                  null,
                                  `service_list.${index}.price`,
                                  parseFloat(e.target.value) || 0,
                                );
                            }}
                            error={!!formErrors[`service_list[${index}].price`]}
                          />
                        </Grid>

                        <Grid item xs={1.3}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            sx={{
                              pt: "9px",
                              ".MuiInputAdornment-root": {
                                position: "absolute",
                                left: "14px",
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ฿
                                </InputAdornment>
                              ),
                            }}
                            value={product?.discount}
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                onChange(
                                  null,
                                  `service_list.${index}.discount`,
                                  e.target.value,
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                onChange(
                                  null,
                                  `service_list.${index}.discount`,
                                  parseFloat(e.target.value) || 0,
                                );
                            }}
                            error={
                              !!formErrors[`service_list[${index}].discount`]
                            }
                          />
                        </Grid>

                        <Grid item xs={1.6}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            sx={{
                              pt: "9px",
                              ".MuiInputAdornment-root": {
                                position: "absolute",
                                left: "14px",
                              },
                            }}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  ฿
                                </InputAdornment>
                              ),
                            }}
                            value={product?.total}
                          />
                        </Grid>

                        <Grid item xs={0.4} sx={{ position: "relative" }}>
                          <IconButton
                            aria-label="delete"
                            disabled={formData?.service_list?.length < 2}
                            onClick={() => {
                              if (!!product.service_type) {
                                setConfirmInfo((info) => ({
                                  ...info,
                                  visible: true,
                                  title:
                                    getLocale() === "th"
                                      ? `คุณต้องการลบการบริการใช่หรือไม่`
                                      : `คุณต้องการลบการบริการใช่หรือไม่`,
                                  confirmFunc: () => {
                                    onRemoveElement(index);
                                    setConfirmInfo(initialConfirm);
                                  },
                                }));
                              } else {
                                onRemoveElement(index);
                              }
                            }}
                            sx={{
                              position: "absolute",
                              top: "10px",
                              right: {
                                xs: "-70%",
                                lg: "-40%",
                              },
                              // top: "50%",
                              // transform: "translate(-40%, -65%);",
                              ":disabled": {
                                ".MuiSvgIcon-root": {
                                  color: "var(--darkgrey-color)",
                                },
                              },
                            }}
                          >
                            <DeleteOutlineIcon
                              sx={{ color: "var(--primary-color)" }}
                            />
                          </IconButton>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2, textAlign: "left" }}>
                          <FormControlLabel
                            label="เพิ่มรายละเอียดการบริการ"
                            sx={{
                              color: "var(--secondary-color)",
                              fontSize: font.size.sm,
                              fontWeight: 400,
                            }}
                            control={
                              <Checkbox
                                sx={{
                                  ":hover": {
                                    ".MuiSvgIcon-root": {
                                      color: "var(--primary-color)",
                                    },
                                  },
                                }}
                                icon={
                                  <RadioButtonUncheckedIcon
                                    sx={{
                                      color: "var(--lightgrey-color)",
                                    }}
                                  />
                                }
                                checkedIcon={
                                  <RadioButtonCheckedIcon
                                    sx={{ color: "var(--primary-color)" }}
                                  />
                                }
                                checked={
                                  product?.description !== "" ? true : false
                                }
                                disabled={
                                  product?.description !== "" ||
                                  product?.service_type === null
                                    ? true
                                    : false
                                }
                                onChange={(e) => {
                                  open(
                                    `service_list.${index}.description.add-service-descripition-modal`,
                                  );
                                }}
                              />
                            }
                          />
                        </Grid>

                        {product?.description !== "" ? (
                          <>
                            <Grid
                              item
                              xs={12}
                              sx={{ mb: 1, textAlign: "left" }}
                            >
                              <Button
                                variant="text"
                                sx={{
                                  px: 3,
                                  py: 1,
                                  borderRadius: 3,
                                }}
                                onClick={() => {
                                  open(
                                    `service_list.${index}.description.add-service-descripition-modal`,
                                    true,
                                  );
                                }}
                              >
                                แนะนำรายละเอียด
                              </Button>
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                variant="outlined"
                                multiline
                                fullWidth
                                sx={{
                                  ".MuiOutlinedInput-root": {
                                    p: 2,
                                  },
                                  ".MuiOutlinedInput-input": {
                                    py: 0,
                                    lineHeight: 1.8,
                                    textAlign: "left",
                                  },
                                }}
                                value={product?.description}
                                onChange={(e) => {
                                  onChange(
                                    e,
                                    `service_list.${index}.description`,
                                  );
                                }}
                                error={
                                  !!formErrors[
                                    `service_list[${index}].description`
                                  ]
                                }
                              />
                            </Grid>
                          </>
                        ) : null}
                      </Grid>
                    </Grid>
                  </>
                ) : product?.service_type !== null ? (
                  <>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 3,
                        fontSize: font.size.sm,
                        borderBottom: "1px solid var(--lightgrey-color)",
                        ":last-child": {
                          borderBottom: "none",
                        },
                      }}
                      key={`service-${index}`}
                    >
                      <Grid
                        container
                        columnSpacing={{ xs: 2, lg: 4 }}
                        sx={{
                          fontSize: font.size.md,
                          ".MuiAutocomplete-root": {
                            margin: 0,
                          },
                          ".MuiTextField-root": {
                            borderRadius: "24px",
                          },
                          ".MuiOutlinedInput-root": {
                            borderRadius: "24px",
                          },
                          ".MuiOutlinedInput-input": {
                            py: "10px",
                            fontSize: font.size.sm,
                            textAlign: "center",
                          },
                          ".MuiInputBase-inputMultiline": {
                            p: 0,
                            textAlign: "left",
                          },
                          ".MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input":
                            {
                              px: 0,
                            },
                        }}
                      >
                        <Grid
                          item
                          xs={12}
                          container
                          columnSpacing={2}
                          onClick={() => {
                            open(
                              `service_list.${index}.service_type.edit-service-detail-modal`,
                            );
                          }}
                        >
                          <Grid item xs={4}>
                            <TextDetail>
                              {product?.service_type?.service_code}
                            </TextDetail>

                            <TextDetail>
                              {`(${product?.service_type?.service_name}/${
                                getLocale() === "th"
                                  ? product?.service_type?.service_unit?.unit_th
                                  : product?.service_type?.service_unit?.unit_en
                              })`}
                            </TextDetail>
                          </Grid>

                          <Grid item xs={1.9}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                              }}
                              value={product?.volume}
                            />
                          </Grid>

                          <Grid item xs={1.9}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              sx={{
                                ".MuiInputAdornment-root": {
                                  position: "absolute",
                                  left: "14px",
                                },
                              }}
                              InputProps={{
                                readOnly: true,
                                // startAdornment: (
                                //   <InputAdornment position="start">
                                //     ฿
                                //   </InputAdornment>
                                // ),
                              }}
                              value={convertPrice(product?.price)}
                            />
                          </Grid>

                          <Grid item xs={1.9}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              sx={{
                                ".MuiInputAdornment-root": {
                                  position: "absolute",
                                  left: "14px",
                                },
                              }}
                              InputProps={{
                                readOnly: true,
                                // startAdornment: (
                                //   <InputAdornment position="start">
                                //     ฿
                                //   </InputAdornment>
                                // ),
                              }}
                              value={convertPrice(product?.discount)}
                            />
                          </Grid>

                          <Grid item xs={2.3}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              sx={{
                                ".MuiInputAdornment-root": {
                                  position: "absolute",
                                  left: "14px",
                                },
                              }}
                              InputProps={{
                                readOnly: true,
                                // startAdornment: (
                                //   <InputAdornment position="start">
                                //     ฿
                                //   </InputAdornment>
                                // ),
                              }}
                              value={convertPrice(product?.total)}
                            />
                          </Grid>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1, textAlign: "left" }}>
                          <FormControlLabel
                            label="เพิ่มรายละเอียดการบริการ"
                            sx={{
                              color: "var(--secondary-color)",
                              fontSize: font.size.sm,
                              fontWeight: 400,
                            }}
                            control={
                              <Checkbox
                                sx={{
                                  ":hover": {
                                    ".MuiSvgIcon-root": {
                                      color: "var(--primary-color)",
                                    },
                                  },
                                }}
                                icon={
                                  <RadioButtonUncheckedIcon
                                    sx={{
                                      color: "var(--lightgrey-color)",
                                    }}
                                  />
                                }
                                checkedIcon={
                                  <RadioButtonCheckedIcon
                                    sx={{ color: "var(--primary-color)" }}
                                  />
                                }
                                checked={
                                  product?.description !== "" ? true : false
                                }
                                disabled={
                                  product?.description !== "" ||
                                  product?.service_type === null
                                    ? true
                                    : false
                                }
                                onChange={(e) => {
                                  open(
                                    `service_list.${index}.description.add-service-descripition-modal`,
                                  );
                                }}
                              />
                            }
                          />
                        </Grid>

                        {product?.description !== "" ? (
                          <>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                              <TextField
                                variant="outlined"
                                multiline
                                fullWidth
                                sx={{
                                  ".MuiOutlinedInput-root": {
                                    p: 2,
                                  },
                                  ".MuiOutlinedInput-input": {
                                    py: 0,
                                    lineHeight: 1.8,
                                    textAlign: "left",
                                  },
                                }}
                                value={product?.description}
                                inputProps={{
                                  readOnly: true,
                                }}
                                onClick={() => {
                                  open(
                                    `service_list.${index}.description.add-service-descripition-modal`,
                                    true,
                                  );
                                }}
                              />
                            </Grid>
                          </>
                        ) : null}
                      </Grid>
                    </Grid>
                  </>
                ) : null}

                {/* edit tablet/mobile service */}
                <EditSheet
                  docType="service"
                  quoItemDetail={product}
                  isOpen={
                    getModalState(
                      `service_list.${index}.service_type.edit-service-detail-modal`,
                    )?.isOpen
                  }
                  onClose={() =>
                    close(
                      `service_list.${index}.service_type.edit-service-detail-modal`,
                    )
                  }
                  onSubmit={(val) => {
                    onChange(
                      null,
                      `service_list.${index}.edit.service_type`,
                      val,
                    );
                  }}
                  onRemoveElement={() => {
                    setConfirmInfo((info) => ({
                      ...info,
                      visible: true,
                      title:
                        getLocale() === "th"
                          ? `คุณต้องการลบการบริการใช่หรือไม่`
                          : `คุณต้องการลบการบริการใช่หรือไม่`,
                      confirmFunc: () => {
                        onRemoveElement(index);
                        close(
                          `service_list.${index}.service_type.edit-service-detail-modal`,
                        );
                        setConfirmInfo(initialConfirm);
                      },
                    }));
                  }}
                  isRemoveDisabled={formData.service_list?.length <= 1}
                />

                {/* add description */}
                <ModalAddition
                  type="desc"
                  descData={product?.description}
                  docType="service"
                  isOpen={
                    getModalState(
                      `service_list.${index}.description.add-service-descripition-modal`,
                    )?.isOpen
                  }
                  isEdit={
                    getModalState(
                      `service_list.${index}.description.add-service-descripition-modal`,
                    )?.isEdit
                  }
                  onClose={() =>
                    close(
                      `service_list.${index}.description.add-service-descripition-modal`,
                    )
                  }
                  onSubmit={(val) => {
                    onChange(null, `service_list.${index}.description`, val);
                    close(
                      `service_list.${index}.description.add-service-descripition-modal`,
                    );
                  }}
                />
              </>
            ))}

          {/* Add product */}
          {deviceWidth > 835 ? (
            <Grid
              item
              xs={12}
              sx={{
                px: { lg: 5, xs: 3 },
                pt: 3,
                pb: 5,
                fontSize: font.size.sm,
              }}
            >
              <Button
                variant="text"
                startIcon={<AddIcon />}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 3,
                }}
                onClick={onAppendElement}
              >
                Add Service
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </WrapperGrid>
    </>
  );
};

export default ServiceDataGrid;
